import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  useReducer,
  useLayoutEffect,
} from 'react'
import { GameModeSwitch } from './components/games/GameModeSwitch'
import { GameStatus } from './components/games/Game'
import { InfoModal } from './components/modals/InfoModal'
import { StatsModal } from './components/modals/StatsModal'
import { HintModal } from './components/modals/HintModal'
import { Footer } from './components/general/Footer'
import { SettingsModal } from './components/modals/SettingsModal'
import { SupportModal } from './components/modals/SupportModal'
import { ProfileModal } from './components/modals/ProfileModal'
import { ArchiveModal } from './components/modals/ArchiveModal'
import { CustomGameModal } from './components/modals/CustomGameModal'
import { OpenPaymentModal } from './components/modals/OpenPaymentModal'
import { ImportStatsModal } from './components/modals/ImportStatsModal'
import { PlusSignupModal } from './components/modals/PlusSignupModal'
import { XsollaSpinner } from './components/spinner/XsollaSpinner'
import { BatchedAchievementModal } from './components/modals/BatchedAchievementModal'
import { AchievementsProvider } from './context/AchievementsContext'
import {
  ResponsiveScreen,
  useMobile,
} from './components/screen/ResponsiveScreen'
import { Plan, getPlanById } from './lib/plans'
import { xsolla } from './lib/xsolla'
import { randomSeed } from './lib/rand'
import {
  setStatsLocalAndRemote,
  DEFAULT_STATS,
  loadLocalStats,
} from './lib/stats'
import {
  GAME_COPIED_MESSAGE,
  HEXPERT_MODE_ALERT_MESSAGE,
  HARD_MODE_ALERT_MESSAGE,
  EXPERT_MODE_ALERT_MESSAGE,
  DISCOURAGE_INAPP_BROWSER_TEXT,
  SIGNIN_MESSAGE,
  SIGNOUT_MESSAGE,
  SHARE_FAILURE_TEXT,
  GAME_MODE_DAILY,
  GAME_MODE_UNLIMITED,
  GAME_MODE_MINI_DAILY,
  GAME_MODE_LOADING,
  GAME_MODE_CUSTOM,
  GA_CATEGORY_SETTINGS,
  GA_CATEGORY_MODALS,
  GA_CATEGORY_NAV,
  GA_ACTION_GAMEMODE_TOGGLE,
  GA_ACTION_DARKMODE_TOGGLE,
  GA_ACTION_HARDMODE_TOGGLE,
  GA_ACTION_EXPERTMODE_TOGGLE,
  GA_ACTION_HIGHCONTRASTMODE_TOGGLE,
  GA_ACTION_INFOMODAL_OPEN,
  GA_ACTION_STATSMODAL_OPEN,
  GA_ACTION_SUPPORTMODAL_OPEN,
  GA_ACTION_PROFILEMODAL_OPEN,
  GA_ACTION_MIGRATESTATSMODAL_OPEN,
  GA_ACTION_ARCHIVEMODAL_OPEN,
  GA_ACTION_CUSTOMMODAL_OPEN,
  GA_ACTION_SETTINGMODAL_OPEN,
  GA_ACTION_MERGESTATS_OPEN,
  GA_ACTION_IMPORTSTATS_OPEN,
  GA_ACTION_PLUSSIGNUP_OPEN,
  GA_ACTION_REFRESH,
  GA_ACTION_OPENPAYMENT_OPEN,
  GA_ACTION_HINT_OPEN,
  ALERT_DATA_SETTING,
  SEARCH_PARAM_UNLIMITED,
  SEARCH_PARAM_PLAN_ID,
  SEARCH_PARAM_SIGNIN,
  SEARCH_PARAM_ARCHIVE,
  SEARCH_PARAM_PPLUS,
  SEARCH_PARAM_CUSTOM,
  SEARCH_PARAM_ADS,
  PLAYABLE_GAME_MODES,
  GameMode,
} from './constants/strings'
import {
  WELCOME_INFO_MODAL_MS,
  DISCOURAGE_INAPP_BROWSERS,
  LONG_ALERT_TIME_MS,
} from './constants/settings'
import { User, authedUserFactory, AuthedUser } from './lib/user'
import {
  getStoredDeviceId,
  getStoredUnlimitedState,
  setStoredUnlimitedState,
  getStoredGameState,
  setStoredGameState,
  setStoredIsHighContrastMode,
  getStoredIsHighContrastMode,
  getStoredDarkMode,
  setStoredDarkMode,
  getStoredHardMode,
  setStoredHardMode,
  getStoredExpertMode,
  setStoredExpertMode,
  getStoredRevealSpeed,
  setStoredRevealSpeed,
  getStoredCursorType,
  setStoredCursorType,
  setStoredFeature,
  getStoredHints,
  setStoredHints,
  GameStats,
} from './lib/localStorage'
import { DAILY_LIST } from './lib/words'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'

import './App.css'
import { AlertContainer } from './components/alerts/AlertContainer'
import { useAlert } from './context/AlertContext'
import { useAchievements } from './context/AchievementsContext'
import { useLoading } from './context/XsollaLoaderContext'
import { Navbar } from './components/navbar/Navbar'
import { isInAppBrowser } from './lib/browser'
import { MigrateStatsModal } from './components/modals/MigrateStatsModal'
import ReactGA from 'react-ga4'

type Props = {
  mode: GameMode
  user: User
  setUser: (user: User) => void
}

function App({ mode, user, setUser }: Props) {
  const {
    showError: showErrorAlert,
    showSuccess: showSuccessAlert,
    clear: clearAlert,
    setIsVisible: setIsAlertVisible,
  } = useAlert()
  const { loading, setLoading } = useLoading()
  const {
    achievements,
    setAchievements,
    isAchievementsModalOpen,
    setIsAchievementsModalOpen,
  } = useAchievements()
  const isMobile = useMobile()
  let { puzzleId, seed, index } =
    useParams<{ puzzleId: string; seed: string; index: string }>()
  const parsedIndex = useMemo(() => (index ? parseInt(index) : null), [index])
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const [hasShownInfo, setHasShownInfo] = useState(
    !!getStoredGameState(GAME_MODE_DAILY) ||
      !!getStoredGameState(GAME_MODE_UNLIMITED)
  )

  const modeIsResponsive = (mode: GameMode) => {
    return PLAYABLE_GAME_MODES.has(mode) ?? false
  }

  const [isResponsive, setIsResponsive] = useState(modeIsResponsive(mode))
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false)
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false)
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false)
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false)
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false)
  const [isCustomModalOpen, setIsCustomModalOpen] = useState(false)
  const [isPlusSignupModalOpen, setIsPlusSignupModalOpen] = useState(false)
  const [isImportStatsModalOpen, setIsImportStatsModalOpen] = useState(false)
  const [isMergeStatsModalOpen, setIsMergeStatsModalOpen] = useState(false)
  const [isMigrateStatsModalOpen, setIsMigrateStatsModalOpen] = useState(false)
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false)
  const [isOpenPaymentModalOpen, setIsOpenPaymentModalOpen] = useState(false)
  const [isHintModalOpen, setIsHintModalOpen] = useState(false)
  const [purchasingPlan, setPurchasingPlan] = useState<Plan | null>(null)
  const [gameStatus, setGameStatus] = useState<GameStatus | null>(null)
  const gameRef = useRef(null)
  const trackGA = useCallback((setting: boolean, action: string) => {
    ReactGA.event({
      category: GA_CATEGORY_MODALS,
      action: action,
      value: +setting,
    })
  }, [])

  // Duplication because useCallback requires inline functions
  const setIsInfoModalOpenGA = useCallback(
    (setting: boolean) => {
      trackGA(setting, GA_ACTION_INFOMODAL_OPEN)
      setIsInfoModalOpen(setting)
    },
    [trackGA]
  )

  const setIsSupportModalOpenGA = useCallback(
    (setting: boolean) => {
      trackGA(setting, GA_ACTION_SUPPORTMODAL_OPEN)
      setIsSupportModalOpen(setting)
    },
    [trackGA]
  )

  const setIsMigrateStatsModalOpenGA = useCallback(
    (setting: boolean) => {
      trackGA(setting, GA_ACTION_MIGRATESTATSMODAL_OPEN)
      setIsMigrateStatsModalOpen(setting)
    },
    [trackGA]
  )

  const setIsSettingsModalOpenGA = useCallback(
    (setting: boolean) => {
      trackGA(setting, GA_ACTION_SETTINGMODAL_OPEN)
      setIsSettingsModalOpen(setting)
    },
    [trackGA]
  )

  const setIsProfileModalOpenGA = useCallback(
    (setting: boolean) => {
      trackGA(setting, GA_ACTION_PROFILEMODAL_OPEN)
      setIsProfileModalOpen(setting)
    },
    [trackGA]
  )

  const setIsArchiveModalOpenGA = useCallback(
    (setting: boolean) => {
      trackGA(setting, GA_ACTION_ARCHIVEMODAL_OPEN)
      setIsArchiveModalOpen(setting)
    },
    [trackGA]
  )

  const setIsCustomModalOpenGA = useCallback(
    (setting: boolean) => {
      trackGA(setting, GA_ACTION_CUSTOMMODAL_OPEN)
      setIsCustomModalOpen(setting)
    },
    [trackGA]
  )

  const setIsImportStatsModalOpenGA = useCallback(
    (setting: boolean) => {
      trackGA(setting, GA_ACTION_IMPORTSTATS_OPEN)
      setIsImportStatsModalOpen(setting)
    },
    [trackGA]
  )

  const setIsMergeStatsModalOpenGA = useCallback(
    (setting: boolean) => {
      trackGA(setting, GA_ACTION_MERGESTATS_OPEN)
      setIsMergeStatsModalOpen(setting)
    },
    [trackGA]
  )

  const setIsPlusSignupModalOpenGA = useCallback(
    (setting: boolean) => {
      trackGA(setting, GA_ACTION_PLUSSIGNUP_OPEN)
      setIsPlusSignupModalOpen(setting)
    },
    [trackGA]
  )

  const setIsOpenPaymentModalOpenGA = useCallback(
    (setting: boolean) => {
      trackGA(setting, GA_ACTION_OPENPAYMENT_OPEN)
      setIsOpenPaymentModalOpen(setting)
    },
    [trackGA]
  )

  const setIsHintModalOpenGA = useCallback(
    (setting: boolean) => {
      trackGA(setting, GA_ACTION_HINT_OPEN)
      setIsHintModalOpen(setting)
    },
    [trackGA]
  )

  // inline this so we can wrap in useCallback
  const setIsStatsModalOpenGA = useCallback((isOpen: boolean) => {
    ReactGA.event({
      category: GA_CATEGORY_MODALS,
      action: GA_ACTION_STATSMODAL_OPEN,
      value: +isOpen,
    })
    setIsStatsModalOpen(isOpen)
  }, [])

  const [isDarkMode, setIsDarkMode] = useState(getStoredDarkMode())
  const [isHighContrastMode, setIsHighContrastMode] = useState(
    getStoredIsHighContrastMode()
  )
  const [enableHints, setEnableHints] = useState(getStoredHints())

  const setStats = useCallback(
    (gameStats: GameStats) => {
      setUser(user.cloneWithNewStats(gameStats))
    },
    [user, setUser]
  )

  const [unlimitedState, setUnlimitedState] = useState(() => {
    let state = getStoredUnlimitedState()
    if (state && state.seed) {
      return state
    }
    return {
      index: state?.index ? state.index : 0,
      seed: randomSeed(),
    }
  })

  const getUnlimitedPuzzleIdWithRetry = useCallback(
    (index: number, seed: string) => {
      let pidAndIndex = DAILY_LIST.getUnlimitedPuzzleId(index, seed)
      if (!pidAndIndex) {
        // exhausted entire list so start over with a new seed
        index = 0
        seed = randomSeed()
        pidAndIndex = DAILY_LIST.getUnlimitedPuzzleId(index, seed)
      }
      if (!pidAndIndex) {
        throw new Error('unlimited pid index out of range despite refresh')
      }
      return { ...pidAndIndex!, seed }
    },
    []
  )

  const puzzleSlug = (pid: string, s: string) => `/${pid}${s ? '/' + s : ''}`

  const getShareUrl = (pid?: string, s?: string) => {
    let url = 'https://www.polygonle.com'
    if (
      mode === GAME_MODE_DAILY ||
      mode === GAME_MODE_MINI_DAILY ||
      !pid ||
      !s
    ) {
      return url
    }
    if (mode === GAME_MODE_CUSTOM) {
      url = `${url}/custom`
    }
    return `${url}${puzzleSlug(pid, s)}`
  }
  const [hardModeUpdater, updateHardMode] = useReducer((x) => x + 1, 0)

  const isHardMode = useMemo(() => {
    const state = getStoredGameState(mode)
    if (state?.hard !== undefined) {
      return state.hard
    }
    return getStoredHardMode()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, hardModeUpdater])

  const [expertModeUpdater, updateExpertMode] = useReducer((x) => x + 1, 0)
  const isExpertMode = useMemo(() => {
    const state = getStoredGameState(mode)
    if (state?.expert !== undefined) {
      return state.expert
    }
    return getStoredExpertMode()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, expertModeUpdater])

  const [revealSpeed, setRevealSpeed] = useState(getStoredRevealSpeed())
  const [cursorType, setCursorType] = useState(getStoredCursorType())

  useEffect(() => {
    if (mode !== GAME_MODE_LOADING) {
      clearAlert()
    }
  }, [mode, clearAlert])

  useEffect(() => {
    if (!hasShownInfo) {
      setTimeout(() => {
        setIsInfoModalOpenGA(true)
      }, WELCOME_INFO_MODAL_MS)
      setHasShownInfo(true)
    }
  }, [hasShownInfo, setIsInfoModalOpenGA])

  useEffect(() => {
    DISCOURAGE_INAPP_BROWSERS &&
      isInAppBrowser() &&
      showErrorAlert(DISCOURAGE_INAPP_BROWSER_TEXT, {
        persist: false,
        durationMs: 7000,
      })
  }, [showErrorAlert])

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }

    if (isHighContrastMode) {
      document.documentElement.classList.add('high-contrast')
    } else {
      document.documentElement.classList.remove('high-contrast')
    }
  }, [isDarkMode, isHighContrastMode])

  const handleDarkMode = (isDark: boolean) => {
    ReactGA.event({
      category: GA_CATEGORY_SETTINGS,
      action: GA_ACTION_DARKMODE_TOGGLE,
    })
    setIsDarkMode(isDark)
    setStoredDarkMode(isDark)
  }

  const clearGameState = useCallback(() => {
    setIsAlertVisible(false)
    clearAlert()
  }, [setIsAlertVisible, clearAlert])

  const detectHexpertMode = (isHard: boolean, isExpert: boolean) => {
    if (isHard && isExpert) {
      showSuccessAlert(HEXPERT_MODE_ALERT_MESSAGE, {
        data: { type: ALERT_DATA_SETTING },
      })
    }
  }

  const handleHardMode = (isHard: boolean) => {
    ReactGA.event({
      category: GA_CATEGORY_SETTINGS,
      action: GA_ACTION_HARDMODE_TOGGLE,
    })
    if (gameStatus?.guesses.length === 0 || isHardMode) {
      setStoredHardMode(isHard) // TODO determine whether or not to update the individual solve
      const state = getStoredGameState(mode)
      if (state?.hard !== undefined) {
        state.hard = isHard
        setStoredGameState(state)
      }
      detectHexpertMode(isHard, isExpertMode)
      updateHardMode()
    } else {
      showErrorAlert(HARD_MODE_ALERT_MESSAGE, {
        data: { type: ALERT_DATA_SETTING },
      })
    }
  }

  const handleExpertMode = (isExpert: boolean) => {
    ReactGA.event({
      category: GA_CATEGORY_SETTINGS,
      action: GA_ACTION_EXPERTMODE_TOGGLE,
    })
    if (gameStatus?.guesses.length === 0 || isExpertMode) {
      setStoredExpertMode(isExpert)
      const state = getStoredGameState(mode)
      if (state?.expert !== undefined) {
        state.expert = isExpert
        setStoredGameState(state)
      }
      detectHexpertMode(isHardMode, isExpert)
      updateExpertMode()
    } else {
      showErrorAlert(EXPERT_MODE_ALERT_MESSAGE, {
        data: { type: ALERT_DATA_SETTING },
      })
    }
  }

  const onPossibleUserSubscriptionChange = useCallback(
    async (event: any, data: any) => {
      if (!(user instanceof AuthedUser)) {
        return
      }
      const subs = await xsolla.getCurrentUserSubscriptions()
      //
      const newFsUser = { ...user.firestoreUser() }
      newFsUser.subscriptions = subs
      const newUser = await authedUserFactory(newFsUser)
      setUser(newUser)
    },
    [user, setUser]
  )

  const onPurchasePlan = useCallback(
    async (plan: Plan) => {
      if (!plan) {
        return
      }

      if (!(user instanceof AuthedUser)) {
        return
      }

      setLoading(true)
      const r = await xsolla.getSubscriptionPlanToken(
        user,
        plan.external_id,
        isMobile
      )
      setLoading(false)
      await xsolla.renderPayStation(r.token)
      xsolla.onPayStationStatusDone(onPossibleUserSubscriptionChange)
    },
    [user, onPossibleUserSubscriptionChange, isMobile, setLoading]
  )

  const onOpenPayment = (plan?: Plan) => {
    setIsOpenPaymentModalOpenGA(false)
    if (plan) {
      onPurchasePlan(plan)
    }
  }

  const onPlusSignupModalClose = (plan?: Plan) => {
    setIsPlusSignupModalOpenGA(false)
    if (plan) {
      onPurchasePlan(plan)
    }
  }

  const handleHighContrastMode = (isHighContrast: boolean) => {
    ReactGA.event({
      category: GA_CATEGORY_SETTINGS,
      action: GA_ACTION_HIGHCONTRASTMODE_TOGGLE,
    })
    setIsHighContrastMode(isHighContrast)
    setStoredIsHighContrastMode(isHighContrast)
  }

  const handleRevealSpeed = (revealSpeed: string) => {
    setRevealSpeed(revealSpeed)
    setStoredRevealSpeed(revealSpeed)
  }

  const handleCursorType = (cursorType: string) => {
    setCursorType(cursorType)
    setStoredCursorType(cursorType)
  }

  const handleHints = (enable: boolean) => {
    setEnableHints(enable)
    setStoredHints(enable)
  }

  const handleHintClick = (w: string) => {
    if (gameRef.current) {
      setIsHintModalOpen(false)
      ;(gameRef.current as any).setHint(w)
    }
  }

  useEffect(() => {
    if (
      !user.isAuthed() ||
      !user.hasPolygonlePlus() ||
      searchParams.has(SEARCH_PARAM_SIGNIN)
    ) {
      // search params will redirect first, wait until the redirect before checking
      return
    }

    const stats = user.stats()
    const syncedDeviceIds = stats.syncedDeviceIds ?? []
    // detect bugged device id
    if (syncedDeviceIds.includes('')) {
      const deviceId = getStoredDeviceId()
      // write it to firestore
      const deviceIds = [...syncedDeviceIds]
      deviceIds[deviceIds.indexOf('')] = deviceId
      stats.syncedDeviceIds = deviceIds
      setStats(stats)
      setStatsLocalAndRemote(user, stats)
      return
    }

    const localStats = loadLocalStats()
    if (
      localStats !== DEFAULT_STATS &&
      user.stats() &&
      !syncedDeviceIds.includes(getStoredDeviceId())
    ) {
      setIsMergeStatsModalOpenGA(true)
    }
  }, [user, setIsMergeStatsModalOpenGA, searchParams, setStats])

  useEffect(() => {
    if (searchParams.has(SEARCH_PARAM_SIGNIN)) {
      showSuccessAlert(SIGNIN_MESSAGE)
      searchParams.delete(SEARCH_PARAM_SIGNIN)
      setSearchParams(searchParams)
    }
  }, [searchParams, showSuccessAlert, setSearchParams])

  useEffect(() => {
    if (searchParams.has(SEARCH_PARAM_ADS)) {
      searchParams.delete(SEARCH_PARAM_ADS)
      setStoredFeature('ads', true)
      setSearchParams(searchParams)
      window.location.reload()
    }
  }, [searchParams, setSearchParams])

  useEffect(() => {
    if (searchParams.has(SEARCH_PARAM_PPLUS)) {
      searchParams.delete(SEARCH_PARAM_PPLUS)
      setSearchParams(searchParams)
      setIsStatsModalOpenGA(false)
      setIsPlusSignupModalOpenGA(true)
    }
  }, [
    searchParams,
    setSearchParams,
    setIsPlusSignupModalOpenGA,
    setIsStatsModalOpenGA,
  ])

  useEffect(() => {
    if (searchParams.has(SEARCH_PARAM_PLAN_ID)) {
      const planId = searchParams.get(SEARCH_PARAM_PLAN_ID)
      setPurchasingPlan(getPlanById(planId!))
      setIsOpenPaymentModalOpenGA(true)
      searchParams.delete(SEARCH_PARAM_PLAN_ID)
      setSearchParams(searchParams)
    }
  }, [
    searchParams,
    setSearchParams,
    onPurchasePlan,
    setIsOpenPaymentModalOpenGA,
  ])

  useEffect(() => {
    if (searchParams.has(SEARCH_PARAM_ARCHIVE)) {
      setIsArchiveModalOpenGA(true)
      searchParams.delete(SEARCH_PARAM_ARCHIVE)
      setSearchParams(searchParams)
    }
  }, [searchParams, setIsArchiveModalOpenGA, setSearchParams, clearAlert])

  useEffect(() => {
    if (searchParams.has(SEARCH_PARAM_CUSTOM)) {
      setIsCustomModalOpenGA(true)
      searchParams.delete(SEARCH_PARAM_CUSTOM)
      setSearchParams(searchParams)
    }
  }, [searchParams, setIsCustomModalOpenGA, setSearchParams, clearAlert])

  useEffect(() => {
    setStoredUnlimitedState(unlimitedState)
  }, [unlimitedState])

  const handleGameMode = useCallback(
    (gameMode: string) => {
      ReactGA.event({
        category: GA_CATEGORY_SETTINGS,
        action: GA_ACTION_GAMEMODE_TOGGLE,
      })

      if (gameMode === GAME_MODE_DAILY) {
        navigate('/')
      } else if (gameMode === GAME_MODE_MINI_DAILY) {
        navigate('/mini')
      } else {
        const { pid, index, seed } = DAILY_LIST.getUnlimitedPuzzleIdWithRetry(
          unlimitedState.index,
          unlimitedState.seed
        )
        unlimitedState.index = index
        navigate(puzzleSlug(pid, seed))
      }
    },
    [navigate, unlimitedState]
  )

  useEffect(() => {
    if (searchParams.has(SEARCH_PARAM_UNLIMITED)) {
      handleGameMode(GAME_MODE_UNLIMITED)
    }
  }, [searchParams, handleGameMode])

  useLayoutEffect(() => {
    setIsResponsive(modeIsResponsive(mode))
  }, [mode])

  const onRefresh = () => {
    ReactGA.event({
      category: GA_CATEGORY_NAV,
      action: GA_ACTION_REFRESH,
    })
    setIsStatsModalOpenGA(false)
    clearGameState()
    const { pid, index, seed } = getUnlimitedPuzzleIdWithRetry(
      unlimitedState.index + 1,
      unlimitedState.seed
    )
    unlimitedState.index = index
    unlimitedState.seed = seed
    setUnlimitedState(unlimitedState)
    setStoredUnlimitedState(unlimitedState)
    navigate(puzzleSlug(pid, seed))
  }

  const onGiveUp = () => {
    if (gameRef.current) {
      ;(gameRef.current as any).giveUp()
    }
  }

  const onStartUnlimited = () => {
    setIsStatsModalOpenGA(false)
    handleGameMode(GAME_MODE_UNLIMITED)
  }

  const onGameStatusChange = useCallback((status: GameStatus) => {
    setGameStatus(status)
  }, [])

  const onUpdateStats = (gameStats: GameStats) => {
    setStats(gameStats)
  }

  const onOpenArchive = () => {
    setIsArchiveModalOpenGA(true)
  }

  const onHint = () => {
    setIsHintModalOpenGA(true)
  }

  const handleProfileClose = (signout: boolean) => {
    if (signout) {
      showSuccessAlert(SIGNOUT_MESSAGE)
    }
    setIsProfileModalOpenGA(false)
  }

  const handleShareToClipboard = () => {
    showSuccessAlert(GAME_COPIED_MESSAGE)
  }

  const handleShareFailure = () => {
    showErrorAlert(SHARE_FAILURE_TEXT, {
      durationMs: LONG_ALERT_TIME_MS,
    })
  }

  const handleAchievementsModalClose = () => {
    setIsAchievementsModalOpen(false)
    setAchievements([])
    if (gameRef.current) {
      ;(gameRef.current as any).handleAchievementsModalClose()
    }
  }

  return (
    <>
      {loading && <XsollaSpinner />}
      <ResponsiveScreen
        id="app"
        className="flex flex-col"
        responsive={isResponsive}
      >
        <Navbar
          user={user}
          setIsInfoModalOpen={setIsInfoModalOpenGA}
          setIsStatsModalOpen={setIsStatsModalOpenGA}
          setIsSettingsModalOpen={setIsSettingsModalOpenGA}
          setIsSupportModalOpen={setIsSupportModalOpenGA}
          setIsProfileModalOpen={setIsProfileModalOpenGA}
          onRefresh={onRefresh}
          onGiveUp={onGiveUp}
          gameMode={mode}
          gameStatus={gameStatus}
        />
        <BatchedAchievementModal
          achievements={achievements.current!}
          isOpen={isAchievementsModalOpen}
          handleClose={handleAchievementsModalClose}
        />
        <GameModeSwitch
          ref={gameRef}
          mode={mode}
          user={user}
          seed={seed!}
          puzzleId={puzzleId!}
          index={parsedIndex}
          settings={{
            isDarkMode,
            isHardMode,
            isExpertMode,
            isHighContrastMode,
            revealSpeed,
            cursorType,
            enableHints,
          }}
          actions={{
            handleShareToClipboard,
            handleShareFailure,
            onStartUnlimited,
            onNextPuzzle: onRefresh,
            onJoinPPlus: () => setIsPlusSignupModalOpenGA(true),
          }}
          setIsStatsModalOpen={setIsStatsModalOpenGA}
          onGameStatusChange={onGameStatusChange}
          onUpdateStats={onUpdateStats}
          onOpenArchive={onOpenArchive}
          onHint={onHint}
          setResponsive={setIsResponsive}
        />
        <InfoModal
          isOpen={isInfoModalOpen}
          isDarkMode={isDarkMode}
          handleClose={() => setIsInfoModalOpenGA(false)}
        />
        <SupportModal
          isOpen={isSupportModalOpen}
          handleClose={() => setIsSupportModalOpenGA(false)}
        />
        <HintModal
          isOpen={isHintModalOpen}
          gameStatus={gameStatus}
          handleClose={() => setIsHintModalOpenGA(false)}
          setIsInfoModalOpen={setIsInfoModalOpenGA}
          handleHintClick={handleHintClick}
        />
        <ArchiveModal
          user={user}
          mode={mode}
          index={parsedIndex}
          isOpen={isArchiveModalOpen}
          isDarkMode={isDarkMode}
          handleClose={() => setIsArchiveModalOpenGA(false)}
        />
        <ProfileModal
          onPayStationClose={onPossibleUserSubscriptionChange}
          user={user}
          isOpen={isProfileModalOpen}
          handleClose={handleProfileClose}
        />
        <StatsModal
          list={DAILY_LIST}
          isOpen={isStatsModalOpen}
          gameStatus={gameStatus}
          handleClose={() => setIsStatsModalOpenGA(false)}
          shareUrl={getShareUrl(puzzleId, seed)}
          handleShareToClipboard={handleShareToClipboard}
          handleShareFailure={handleShareFailure}
          handleMigrateStatsButton={() => {
            setIsStatsModalOpenGA(false)
            setIsMigrateStatsModalOpenGA(true)
          }}
          isHardMode={isHardMode}
          isExpertMode={isExpertMode}
          isDarkMode={isDarkMode}
          isHighContrastMode={isHighContrastMode}
          onNextPuzzle={onRefresh}
          onStartUnlimited={onStartUnlimited}
          onJoinPPlus={() => setIsPlusSignupModalOpenGA(true)}
          gameMode={mode}
          user={user}
        />
        <MigrateStatsModal
          user={user}
          isOpen={isMigrateStatsModalOpen}
          handleClose={() => setIsMigrateStatsModalOpenGA(false)}
        />
        <SettingsModal
          isOpen={isSettingsModalOpen}
          handleClose={() => setIsSettingsModalOpenGA(false)}
          isHardMode={isHardMode}
          handleHardMode={handleHardMode}
          isExpertMode={isExpertMode}
          handleExpertMode={handleExpertMode}
          isDarkMode={isDarkMode}
          handleDarkMode={handleDarkMode}
          isHighContrastMode={isHighContrastMode}
          handleHighContrastMode={handleHighContrastMode}
          revealSpeed={revealSpeed}
          handleRevealSpeed={handleRevealSpeed}
          cursorType={cursorType}
          handleCursorType={handleCursorType}
          enableHints={enableHints}
          handleHints={handleHints}
        />
        <PlusSignupModal
          user={user}
          isOpen={isPlusSignupModalOpen}
          handleClose={onPlusSignupModalClose}
        />
        <CustomGameModal
          user={user}
          isOpen={isCustomModalOpen}
          isDarkMode={isDarkMode}
          handleClose={() => setIsCustomModalOpenGA(false)}
        />
        {purchasingPlan && (
          <OpenPaymentModal
            isOpen={isOpenPaymentModalOpen}
            plan={purchasingPlan}
            openPayment={onOpenPayment}
          />
        )}
        {user.isAuthed() && (
          <ImportStatsModal
            merge={false}
            user={user}
            isOpen={isImportStatsModalOpen}
            handleClose={() => setIsImportStatsModalOpenGA(false)}
            onUpdateStats={onUpdateStats}
          />
        )}
        {user.isAuthed() && (
          <ImportStatsModal
            merge={true}
            user={user}
            isOpen={isMergeStatsModalOpen}
            handleClose={() => {
              setIsMergeStatsModalOpenGA(false)
            }}
            onUpdateStats={onUpdateStats}
          />
        )}
        <AlertContainer />
      </ResponsiveScreen>
      <div className="flex justify-center items-center flex-col">
        <Footer
          user={user}
          onJoinPPlus={() => setIsPlusSignupModalOpenGA(true)}
        />
      </div>
    </>
  )
}

export default App
